// Sentinel + label for logs with no routineName: generic "Log a Workout" form
// writes and all pre-existing history (migrated + anything logged before the
// routineName field existed). Kept out of the routineName string-space so it
// can never collide with a real routine literally named "Freestyle".
export const FREESTYLE_KEY = "__freestyle__";
export const FREESTYLE_LABEL = "Freestyle";

// The calendar-day key used throughout this app's day-grouped views
// (year-month-date, local time, month 0-indexed per JS Date convention) --
// exported so anything that needs to key off "which day" without grouping a
// whole logs array (e.g. exerciseNotesService, keying a note to "today")
// uses the exact same format groupByDay below produces, rather than a
// second ad hoc implementation that could drift out of sync.
export const dayKeyFor = (date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

// Group logs into calendar-day buckets. logs is expected newest-first
// (Firestore orderBy date desc), so a straight reduce that appends to the
// current group preserves that ordering both across groups (newest day
// first) and within each group (newest set first). Exported on its own
// (not just an internal step of groupLogs below) since ExerciseHistoryModal
// needs the same day-bucketing for a single exercise's history, with no
// routine sub-grouping on top.
export const groupByDay = (logs) => {
  const groups = [];
  let currentKey = null;

  logs.forEach((log) => {
    const day = log.date?.toDate ? log.date.toDate() : null;
    // Fall back to a stable "Unknown" bucket if a log somehow lacks a date.
    const key = day ? dayKeyFor(day) : "unknown";
    const header = day
      ? day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
      : "Undated";

    if (key !== currentKey) {
      groups.push({ key, header, entries: [] });
      currentKey = key;
    }
    groups[groups.length - 1].entries.push(log);
  });

  return groups;
};

// Sub-groups each day bucket by routineName into collapsible panels, for the
// "Previously..." history list.
export const groupLogs = (logs) => {
  const groups = groupByDay(logs);

  // A Map preserves first-appearance order, which — since entries arrive
  // newest-first — keeps the most recently trained routine's panel at the top
  // of each day. Entries without a routineName fall into a single "Freestyle" panel.
  return groups.map((group) => {
    const routineMap = new Map();
    group.entries.forEach((log) => {
      const routineName = log.routineName || null;
      const routineKey = routineName || FREESTYLE_KEY;
      if (!routineMap.has(routineKey)) {
        routineMap.set(routineKey, {
          panelKey: `${group.key}::${routineKey}`,
          label: routineName || FREESTYLE_LABEL,
          entries: []
        });
      }
      routineMap.get(routineKey).entries.push(log);
    });
    return { ...group, routineGroups: Array.from(routineMap.values()) };
  });
};
